import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

type ThisProjectMessages = {
    see: string,
    forMoreDetail: string,
    stateDepartmentEmailAddress: string,
    notYourAccount: string,
    switchNow: string,
    otherMembers: string,
    landing: {
        title: string,
        subtitle: string,
        pleaseNote: string,
        verifyMilitary: string,
        verifyStateDepartment: string,
    },
    step: {
        personalInfo: {
            military: {
                title: string,
                subtitle: string,
                howDoesVerifyingWorkDetails: string,
            },
            stateDepartment: {
                title: string,
                subtitle: string,
                howDoesVerifyingWorkDetails: string,
            },
        },
        docUpload: {
            military: {
                governmentIssued: string,
                submitButtonLabel: string,
                howDoesVerifyingWorkDetails: string,
            },
            stateDepartment: {
                governmentIssued: string,
                submitButtonLabel: string,
                howDoesVerifyingWorkDetails: string,
            },
            uploadInfo: {
                servingAbroad: string,
                servingAbroadMilitary:string,
                dutyStation:string,
                speedUp:string,
                military: {
                    serviceBranch: string,
                },
                stateDepartment: {
                    currentEmployment: string,
                },
            },
        },
        success: {
            military: {
                title: string,
            },
            stateDepartment: {
                title: string,
            },
        },
        emailLoop: {
            didntReceive: string,
        },
    },
};

export const messagesEN: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    landing: {
        title: 'Verify your government status',
        subtitle: 'Verify your status as an active duty/reservist military or State Department employee serving abroad, and Google Fi will process your exception request.',
        pleaseNote: 'Please note that your Fi account must either be currently <a class="sid-link" href="https://support.google.com/fi/answer/6078618" target="_blank" rel="noreferrer">active</a> when applying for an international roaming exception or <a class="sid-link" href="https://support.google.com/fi/answer/6079346" target="_blank" rel="noreferrer">paused</a> for an extended pausing exception.',
        verifyMilitary: 'Verify your military status',
        verifyStateDepartment: 'Verify your state department employment',
    },
    otherMembers: 'If you have dependents, add them to your group plan before proceeding. Once your status is verified, your group plan members will receive the same exception. Dependents should not submit a separate request.',
    privacyPolicy: 'SheerID Privacy Policy.',
    notYourAccount: 'Not your Google Fi account?',
    switchNow: 'Switch now',
    see: 'See',
    forMoreDetail: 'for more detail.',
    statusPlaceholder: 'Status',
    branchOfServicePlaceholder: 'Branch of service',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder: 'Last name',
    emailAddressPlaceholder: 'Email address',
    stateDepartmentEmailAddress: 'Official State Department email address',
    memberIdPlaceholder: 'Employee ID Number',
    emailExplanation: '(Associated with Google Fi account)',
    verificationPurposesOnly: '(Used for verification purposes only)',
    verifyMyMilitaryStatus: 'Next',
    verifyMyMembershipStatus: 'Verify your State Department email',
    infoShared1: 'ID verification service provided by SheerID. Any information entered into this form will be processed by SheerID for verification purposes and treated according to the ',
    sheeridFaqs: 'Google Fi FAQs',
    errorId: {
        fraudRulesReject: 'We\'re unable to verify you using the information submitted. Please check your information for accuracy, and only use your own mobile device or computer.<br><br>Still having trouble? <a class="sid-link" href="{feedbackUrl}&pid={programId}&locale={locale}" target="_blank" rel="noreferrer">Contact SheerID\'s support team</a> to confirm your identity.',
    },
    step: {
        personalInfo: {
            military: {
                title: 'Verify your military status',
                subtitle: 'Verify you\'re a member of the armed forces serving abroad.',
                howDoesVerifyingWorkDetails: `
                This offer is available to military personnel in the United States Armed Forces who are serving abroad.
                <br><br>
                {companyName} uses SheerID, a trusted partner, to verify that you are a member of the military. SheerID only collects information
                that assists in verifying your eligibility. Additional information or documentation may be requested.`,
            },
            stateDepartment: {
                title: 'Verify your State <br /> Department employment',
                subtitle: 'Verify you\'re an employee of the State Department who is serving abroad. After you\'ve verified your Official State Department email address, we\'ll ask you to submit documentation verifying your deployment status.',
                howDoesVerifyingWorkDetails: `
                This offer is available to employees of the United States Department of State who are serving abroad.
                <br><br>
                {companyName} uses SheerID, a trusted partner, to verify that you are a State Department employee. SheerID only collects information
                that assists in verifying your eligibility. Additional information or documentation may be requested.`,
            },
        },
        docUpload: {
            title: 'Next, upload your documentation',
            fileInstructions: 'Drag and drop your files here or <span class="sid-dropzone-wrap--link sid-link">browse</span>',
            addFile: 'Add files',
            uploadInstructions: 'Upload your government-issued documents',
            military: {
                governmentIssued: 'This should be a typical government-issued document (such as orders or deployment papers). It must include:',
                submitButtonLabel: 'Verify your military status',
                howDoesVerifyingWorkDetails: `Document review is handled by SheerID, a trusted partner who specializes in verifying military.
                <br><br>
                Documents are used only for verifying your military status is current and will be permanently deleted after review is finalized.
                They will not be shared with {companyName}.`,
            },
            stateDepartment: {
                governmentIssued: 'This should be a typical government-issued document. It must include:',
                submitButtonLabel: 'Verify your State Department status',
                howDoesVerifyingWorkDetails: `Document review is handled by SheerID, a trusted partner who specializes in verifying employees.
                <br><br>
                Documents are used only for verifying your State Department employment status is current and will be permanently deleted after review is finalized.
                They will not be shared with {companyName}.`,
            },
            uploadInfo: {
                servingAbroad: 'Indication that you\'re serving abroad',
                servingAbroadMilitary: 'Proof that you\'re stationed outside of the United States',
                dutyStation: 'Duty Station',
                speedUp: 'To speed up the review process, we recommend highlighting this information on the uploaded files.',
                military: {
                    serviceBranch: 'Your branch of service',
                    currentAffiliation: 'Your current affiliation with the armed services',
                },
                stateDepartment: {
                    currentEmployment: 'Your current employment with the State Department',
                },
            },
        },
        pending: {
            titleReview: 'We\'re reviewing your documents',
            turnaroundTime: 'SheerID is reviewing your documents to confirm your status. This process typically takes {estReviewTime}, and in rare times of high volume may take up to {maxReviewTime}.',
            subtitle2: 'Once we\'ve confirmed your status, we\'ll send an email from verify@sheerid.com with the outcome of your review.',
        },
        success: {
            subtitle: 'Your documentation was successfully verified, and Google Fi is now processing your exception. This usually takes a few minutes, but up to a day in some cases. You\'ll get a confirmation email when it\'s approved.',
            military: {
                title: 'Your military status has been verified',
            },
            stateDepartment: {
                title: 'Your State Department employment has been verified',
            },
        },
        emailLoop: {
            title: 'Verify your <br /> State Department email',
            subtitleWithEmail: 'We sent a verification email to <strong>{email}</strong>.',
            subtitleWithoutEmail: 'We sent a verification email to your email.',
            subtitle2: 'Click the link in the email to continue verifying.',
            resendButton: 'Resend',
            didntReceive: 'Didn\'t receive the email?',
        },
    },
};
